import { useEffect, useRef, useState } from 'react';
import { useSpring } from 'react-spring';
import { useDrag } from '@use-gesture/react';
import { clamp } from 'lodash';
import { eventSubscription } from '../../../../../utils/events/eventSubscription';

const settings = {
  area: {
    width: 1440,
    height: 980,
    velocityMultiplier: 180,
  },
};

const { area } = settings;

export default function useMoveLogic() {
  const [bounding, setBounding] = useState({ width: 0, height: 0, x: {}, y: {} });

  const offsetData = useRef({ x: 0, y: 0 });

  const [{ x: xFunc, y: yFunc }, api] = useSpring(() => ({ ...offsetData.current }));

  const animatedDrag = (x, y, immediate = false, onComplete) => {
    if (immediate) {
      api.start({ x, y, immediate: true });
      offsetData.current = { x, y };
      onComplete?.();
    } else {
      api.start({
        x,
        y,
        immediate: false,
        config: { tension: 280, friction: 60 },
        onChange({ value: { x, y } }) {
          offsetData.current = { x, y };
        },
        onRest: onComplete,
      });
    }
  };

  const handlers = useDrag((dragData) => {
    dragData.event.preventDefault();

    const {
      down,
      delta: [dx, dy],
      velocity: [vx, vy],
      direction: [dirX, dirY],
    } = dragData;

    const { x, y } = offsetData.current;
    const {
      x: { min: minX, max: maxX },
      y: { min: minY, max: maxY },
    } = bounding;
    const [newX, newY] = [clamp(x + dx, minX, maxX), clamp(y + dy, minY, maxY)];

    if (down) animatedDrag(newX, newY, true);
    else {
      const { velocityMultiplier } = area;
      const [newXValue, newYValue] = [
        clamp(newX + vx * velocityMultiplier * dirX, minX, maxX),
        clamp(newY + vy * velocityMultiplier * dirY, minY, maxY),
      ];
      animatedDrag(newXValue, newYValue);
    }
  });

  useEffect(() => {
    const onResize = () => {
      const { innerWidth, innerHeight } = global;
      const { width, height } = area;

      const scale = Math.max(Math.max(innerWidth / width, 1), Math.max(innerHeight / height, 1));

      const widthValue = width * scale;
      const heightValue = height * scale;

      setBounding({
        width: widthValue,
        height: heightValue,
        x: { max: (widthValue - innerWidth) / 2, min: -(widthValue - innerWidth) / 2 },
        y: { max: (heightValue - innerHeight) / 2, min: -(heightValue - innerHeight) / 2 },
      });
    };

    onResize();

    return eventSubscription({
      callbacksBus: [{ event: 'resize', callback: onResize }],
    });
  }, []);

  return { animatedDrag, bounding, offsetData, handlers, xFunc, yFunc };
}
